import { Fragment, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import InputContainerButton from "@/components/elements/InputContainer";
import { Popover } from "@/components/elements/Popover";
import { Calendar } from "@/components/modules/Calendar";
import dayjs from "@/utils/date";
import { XIcon } from "lucide-react";

import Button from "../../primitives/Button";
import { FormatDate } from "../FormatTime";

export type DateInputPreset = {
  label: string;
  date: Date;
  group?: string;
};

export default function DateInput({
  placeholder = "Pick a date...",
  date,
  setDate,
  minDate,
  presets,
}: {
  date: Date | null;
  setDate: (date: Date | null) => any;
  placeholder?: string;
  minDate?: Date;
  presets?: DateInputPreset[];
}) {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "bottom-start",
    modifiers: [
      { name: "preventOverflow", enabled: true },
      { name: "offset", options: { offset: [0, 10] } },
    ],
  });

  const lowerBound = dayjs(minDate ?? "1990/01/01")
    .startOf("day")
    .toDate();
  const today = dayjs().endOf("day").toDate();

  return (
    <Popover>
      {({ open }) => (
        <div className="relative w-1/2 pr-2">
          <Popover.Button
            as={InputContainerButton}
            ref={setReferenceElement}
            isDisabled={false}
            isFocused={open}
            className={`py-2 pl-2 text-left text-base ${!date ? "text-gray-400" : ""}`}
          >
            {date ? (
              <FormatDate
                date={date}
                options={{ year: "numeric", month: "short", day: "numeric" }}
              />
            ) : (
              placeholder
            )}
          </Popover.Button>
          {date && (
            <div className="absolute top-0 right-0 flex h-full items-center pr-5">
              <Button
                aria-label="Clear date"
                title="Clear date"
                className="cursor-default text-gray-400 hover:text-gray-500"
                onClick={() => setDate(null)}
              >
                <XIcon className="size-5" />
              </Button>
            </div>
          )}
          {createPortal(
            <Popover.Panel
              ref={setPopperElement}
              className="absolute z-50 w-auto overflow-hidden rounded-xs border bg-popover shadow-lg"
              style={styles.popper}
              {...attributes.popper}
            >
              {({ close }) => (
                <div>
                  <div className="flex">
                    {presets && presets.length > 0 && (
                      <div className="flex w-36 flex-col gap-1 border-r p-2">
                        {presets.map((preset, index) => {
                          const disabled =
                            dayjs(preset.date).isBefore(lowerBound, "day") ||
                            dayjs(preset.date).isAfter(today, "day");
                          const showSeparator =
                            index > 0 &&
                            preset.group !== presets[index - 1].group;
                          return (
                            <Fragment key={preset.label}>
                              {showSeparator && (
                                <div className="my-1 border-t" />
                              )}
                              <button
                                type="button"
                                disabled={disabled}
                                onClick={() => {
                                  setDate(preset.date);
                                  close();
                                }}
                                className={`rounded-xs px-2 py-1.5 text-left text-sm transition-colors ${
                                  date && dayjs(date).isSame(preset.date, "day")
                                    ? "bg-primary text-primary-foreground"
                                    : "hover:bg-accent"
                                } disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent`}
                              >
                                {preset.label}
                              </button>
                            </Fragment>
                          );
                        })}
                      </div>
                    )}
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      defaultMonth={date ?? undefined}
                      startMonth={lowerBound}
                      endMonth={today}
                      disabled={{ before: lowerBound, after: today }}
                      selected={date ?? undefined}
                      onSelect={(selected) => {
                        setDate(selected ?? null);
                        close();
                      }}
                    />
                  </div>
                  <div className="flex justify-end border-t p-2">
                    <button
                      type="button"
                      disabled={!date}
                      onClick={() => {
                        setDate(null);
                        close();
                      }}
                      className="rounded-xs px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </Popover.Panel>,
            document.body,
          )}
        </div>
      )}
    </Popover>
  );
}
